import React, { useEffect, useMemo } from 'react';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

export default function CashRequestDecisionModal({
  show,
  mode,
  requestItem,
  session,
  previousBalance,
  canEditOpeningAmounts = false,
  onHide,
  onSubmit,
}) {
  const isReject = mode === 'reject';
  const isApproveClose = mode === 'approve-close';
  const isApproveOpen = !isReject && requestItem?.requestType === 'OPEN';

  const openingInfo = useMemo(() => {
    const payload = requestItem?.payload || {};
    return {
      previousUsd: Number(payload.previousRealBalanceUsd ?? previousBalance?.usd ?? 0),
      previousHtg: Number(payload.previousRealBalanceHtg ?? previousBalance?.htg ?? 0),
      requestedUsd: Number(payload.requestedOpenUsd ?? payload.openUsd ?? 0),
      requestedHtg: Number(payload.requestedOpenHtg ?? payload.openHtg ?? 0),
    };
  }, [requestItem, previousBalance]);

  const defaultValues = useMemo(() => ({
    openUsd: openingInfo.requestedUsd,
    openHtg: openingInfo.requestedHtg,
    realBalanceUsd: session?.systemBalanceUsd ?? '',
    realBalanceHtg: session?.systemBalanceHtg ?? '',
    note: '',
  }), [openingInfo, session]);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues });

  useEffect(() => {
    if (show) reset(defaultValues);
  }, [show, defaultValues, reset]);

  const title = isReject
    ? 'Rejeter la demande'
    : isApproveClose
      ? 'Confirmer la clôture'
      : isApproveOpen
        ? 'Confirmer la demande d’ouverture'
        : 'Confirmer la demande';

  return (
    <Modal show={show} onHide={onHide} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          {isApproveOpen && (
            <>
              <Alert variant="light" className="border mb-3">
                <Row className="g-2">
                  <Col md={6}>
                    <div className="text-muted small">Solde antérieur réel USD</div>
                    <div className="fw-semibold">{openingInfo.previousUsd.toFixed(2)} USD</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted small">Solde antérieur réel HTG</div>
                    <div className="fw-semibold">{openingInfo.previousHtg.toFixed(2)} HTG</div>
                  </Col>
                </Row>
              </Alert>

              <Alert variant="light" className="border mb-3">
                <Row className="g-2">
                  <Col md={6}>
                    <div className="text-muted small">Montant demandé USD</div>
                    <div className="fw-semibold">{openingInfo.requestedUsd.toFixed(2)} USD</div>
                  </Col>
                  <Col md={6}>
                    <div className="text-muted small">Montant demandé HTG</div>
                    <div className="fw-semibold">{openingInfo.requestedHtg.toFixed(2)} HTG</div>
                  </Col>
                </Row>
              </Alert>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Montant d’ouverture USD</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    readOnly={!canEditOpeningAmounts}
                    {...register('openUsd', {
                      required: 'Requis',
                      validate: (value) => Number(value) >= 0 || 'Le montant doit être supérieur ou égal à 0',
                    })}
                  />
                  {errors.openUsd && <Form.Text className="text-danger">{errors.openUsd.message}</Form.Text>}
                </Col>

                <Col md={6}>
                  <Form.Label>Montant d’ouverture HTG</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    readOnly={!canEditOpeningAmounts}
                    {...register('openHtg', {
                      required: 'Requis',
                      validate: (value) => Number(value) >= 0 || 'Le montant doit être supérieur ou égal à 0',
                    })}
                  />
                  {errors.openHtg && <Form.Text className="text-danger">{errors.openHtg.message}</Form.Text>}
                </Col>
              </Row>

              <div className="small text-muted mt-2">
                {canEditOpeningAmounts
                  ? 'Les montants d’ouverture sont préremplis avec les montants demandés et peuvent être modifiés avant confirmation.'
                  : 'Les montants d’ouverture correspondent à la demande reçue et ne peuvent pas être modifiés.'}
              </div>
            </>
          )}

          {isApproveClose && (
            <>
              <Alert variant="info" className="mb-3">
                Renseigne le solde réel constaté par l’administrateur. Le système calculera automatiquement l’écart administrateur et conservera aussi l’écart déclaré par l’utilisateur.
              </Alert>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label>Solde réel admin USD</Form.Label>
                  <Form.Control type="number" step="0.01" {...register('realBalanceUsd')} />
                </Col>
                <Col md={6}>
                  <Form.Label>Solde réel admin HTG</Form.Label>
                  <Form.Control type="number" step="0.01" {...register('realBalanceHtg')} />
                </Col>
              </Row>
              <div className="small text-muted mt-2">
                Solde système: USD {Number(session?.systemBalanceUsd || 0).toFixed(2)} / HTG {Number(session?.systemBalanceHtg || 0).toFixed(2)}
              </div>
              {requestItem?.payload && (
                <div className="small text-muted mt-1">
                  Déclaré par l’utilisateur: USD {Number(requestItem.payload.requesterRealBalanceUsd || 0).toFixed(2)} / HTG {Number(requestItem.payload.requesterRealBalanceHtg || 0).toFixed(2)}
                </div>
              )}
            </>
          )}

          <Form.Group className="mt-3">
            <Form.Label>{isReject ? 'Motif du rejet' : 'Note de confirmation'}</Form.Label>
            <Form.Control as="textarea" rows={3} {...register('note')} />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Annuler</Button>
          <Button variant={isReject ? 'danger' : 'primary'} type="submit">
            {isReject ? 'Rejeter' : 'Confirmer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
